import React from "react";
import { useParams, Link } from "react-router-dom";
import "./Services.css";
import SEO from "../SEO";
import NotFound from "../NotFound";
import { services, slugify } from "../../seo/routes";
import { buildBreadcrumbJsonLd, DENTIST_ID, SITE_URL } from "../../seo/business";

// Uses Semantic UI's card class names, styled by semantic-subset.css
const ServiceCard = ({ color, title, description }) => (
  <div className={`ui ${color} card service-card`}>
    <div className="content">
      <div className="header">{title}</div>
      <div className="description">{description}</div>
    </div>
  </div>
);

const ServiceMenu = ({ activeCategory }) => (
  <nav className="secondary-menu sticky-menu" aria-label="Service categories">
    {services.map((service) => (
      <Link
        key={service.title}
        to={`/services/${slugify(service.title)}`}
        className={activeCategory === service.title ? "link active fade-in" : "link"}
        aria-current={activeCategory === service.title ? "page" : undefined}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        {service.title}
      </Link>
    ))}
  </nav>
);

const colors = ["pink", "blue", "green", "yellow", "red", "purple"];

const Services = () => {
  const { category } = useParams();

  // The URL slug picks the category; /services shows the first one
  const categoryService = category && services.find((s) => slugify(s.title) === category);
  if (category && !categoryService) return <NotFound />;
  const activeService = categoryService || services[0];
  const activeCategory = activeService.title;
  const activeIndex = services.indexOf(activeService);

  const seo = categoryService
    ? {
        fullTitle: `${categoryService.title} Dentistry in Bensonhurst, Brooklyn`,
        description: `${categoryService.title} dentistry at Benson Dental Studio in Bensonhurst, Brooklyn: ${categoryService.cards
          .map((c) => c.title)
          .join(", ")}. Serving Sheepshead Bay, Bay Ridge & South Brooklyn.`,
        canonical: `/services/${slugify(categoryService.title)}`,
      }
    : {
        title: "Dental Services in Bensonhurst, Brooklyn",
        description:
          "Cosmetic, restorative, preventive and implant dentistry, root canals, gum care and oral surgery in Bensonhurst, Brooklyn. Serving Sheepshead Bay, Bay Ridge & South Brooklyn.",
        canonical: "/services",
      };

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Dental Services", path: "/services" },
    ...(categoryService ? [{ name: `${categoryService.title} Dentistry`, path: seo.canonical }] : []),
  ];

  const jsonLd = [
    buildBreadcrumbJsonLd(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: seo.fullTitle || seo.title,
      url: `${SITE_URL}${seo.canonical}`,
      about: { "@id": DENTIST_ID },
      mainEntity: {
        "@type": "OfferCatalog",
        name: categoryService ? `${categoryService.title} Dentistry` : "Dental Services",
        itemListElement: (categoryService ? [categoryService] : services).flatMap((service) =>
          service.cards.map((card) => ({
            "@type": "Offer",
            offeredBy: { "@id": DENTIST_ID },
            itemOffered: {
              "@type": "MedicalProcedure",
              name: card.title,
              description: card.description,
            },
          }))
        ),
      },
    },
  ];

  return (
    <main className="center-container fade-in" id="main-content">
      <SEO title={seo.title} fullTitle={seo.fullTitle} description={seo.description} canonical={seo.canonical} jsonLd={jsonLd} />
      {categoryService && (
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/services">Dental Services</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">{categoryService.title} Dentistry</span>
        </nav>
      )}
      <header className="services-header">
        {categoryService ? (
          <>
            <h1>{categoryService.title} Dentistry in Bensonhurst, Brooklyn</h1>
            <p>
              {categoryService.title} dental care at Benson Dental Studio, serving
              Bensonhurst, Sheepshead Bay, Bay Ridge, and all of South Brooklyn
            </p>
          </>
        ) : (
          <>
            <h1>Our Dental Services</h1>
            <p>Comprehensive dental care in Bensonhurst, Brooklyn, NY</p>
          </>
        )}
      </header>
      <ServiceMenu activeCategory={activeCategory} />
      <section className="fade-in" aria-labelledby="service-heading">
        <div className="ui horizontal divider service-category-divider">
          <h2 id="service-heading">{activeService.title} Treatments</h2>
        </div>
        <div className="ui doubling two cards">
          {activeService.cards.map((card) => (
            <ServiceCard
              color={colors[activeIndex % colors.length]}
              key={card.title}
              title={card.title}
              description={card.description}
            />
          ))}
        </div>
      </section>
    </main>
  );
};

export default Services;
